/**
 * src/components/student-dashboard-components/theme/components/Input.js
 *
 * React Component Module.
 *
 * Purpose: React UI module containing one or more components and local UI helpers.
 *
 * Data flow (high level):
 * - UI/Caller -> this module -> downstream helpers/services -> state/response.
 */
import React, { useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../ThemeContext';
import { getColor, getTypography, createTransition } from '../themeUtils';

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  width: 100%;
`;

const Label = styled.label`
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.small.fontSize : 
    props.theme.typography.desktop.small.fontSize};
  font-weight: 500;
  color: ${getColor('primary.blue')};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: ${props => props.$size === 'small' ? '0.5rem 0.75rem' : 
                     props.$size === 'large' ? '0.875rem 1rem' : '0.75rem 1rem'};
  border: 2px solid ${props => props.$hasError ? getColor('semantic.error') : getColor('neutral.medium')};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${getColor('neutral.white')};
  color: ${getColor('neutral.black')};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  outline: none;
  ${createTransition('all', 'fast')};
  
  ${props => {
    const typography = getTypography('body', props.theme.isMobile);
    return `
      font-size: ${typography.fontSize};
      line-height: ${typography.lineHeight};
    `;
  }}
  
  &::placeholder {
    color: ${getColor('neutral.dark')};
    opacity: 0.7;
  }
  
  &:focus {
    border-color: ${props => props.$hasError ? getColor('semantic.error') : getColor('primary.skyBlue')};
    box-shadow: 0 0 0 3px ${props => props.$hasError ? 
      'rgba(239, 68, 68, 0.1)' : 'rgba(88, 160, 200, 0.1)'};
  }
  
  &:disabled {
    background: ${getColor('neutral.light')};
    color: ${getColor('neutral.dark')};
    cursor: not-allowed;
    opacity: 0.7;
  }
  
  ${props => props.$hasIcon && `
    padding-left: 2.5rem;
  `}
  
  ${props => props.theme.isMobile && `
    min-height: 44px;
    font-size: 16px; /* Prevents zoom on iOS */
  `}
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid ${props => props.$hasError ? getColor('semantic.error') : getColor('neutral.medium')};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${getColor('neutral.white')};
  color: ${getColor('neutral.black')};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  outline: none;
  resize: vertical;
  min-height: 100px;
  ${createTransition('all', 'fast')};
  
  ${props => {
    const typography = getTypography('body', props.theme.isMobile);
    return `
      font-size: ${typography.fontSize};
      line-height: ${typography.lineHeight};
    `;
  }}
  
  &::placeholder {
    color: ${getColor('neutral.dark')};
    opacity: 0.7;
  }
  
  &:focus {
    border-color: ${props => props.$hasError ? getColor('semantic.error') : getColor('primary.skyBlue')};
    box-shadow: 0 0 0 3px ${props => props.$hasError ? 
      'rgba(239, 68, 68, 0.1)' : 'rgba(88, 160, 200, 0.1)'};
  }
  
  &:disabled {
    background: ${getColor('neutral.light')};
    color: ${getColor('neutral.dark')};
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const IconContainer = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${getColor('neutral.dark')};
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ErrorMessage = styled.span`
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.caption.fontSize : 
    props.theme.typography.desktop.caption.fontSize};
  color: ${getColor('semantic.error')};
  margin-top: ${props => props.theme.spacing.xs};
`;

const HelperText = styled.span`
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.caption.fontSize : 
    props.theme.typography.desktop.caption.fontSize};
  color: ${getColor('neutral.dark')};
  margin-top: ${props => props.theme.spacing.xs};
`;

/**
 * Input.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const Input = ({
  label,
  type = 'text',
  size = 'medium',
  error,
  helperText,
  icon,
  multiline = false,
  rows = 4,
  className,
  ...props
}) => {
  const theme = useTheme();
  const [, setFocused] = useState(false);
  
  const InputComponent = multiline ? StyledTextArea : StyledInput;
  
  return (
    <InputWrapper theme={theme} className={className}>
      {label && (
        <Label theme={theme}>
          {label}
        </Label>
      )}
      
      <InputContainer>
        {icon && (
          <IconContainer>
            {icon}
          </IconContainer>
        )}
        
        <InputComponent
          theme={theme}
          type={multiline ? undefined : type}
          $size={size}
          $hasError={!!error}
          $hasIcon={!!icon}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          rows={multiline ? rows : undefined}
          {...props}
        />
      </InputContainer>
      
      {error && (
        <ErrorMessage theme={theme}>
          {error}
        </ErrorMessage>
      )}
      
      {!error && helperText && (
        <HelperText theme={theme}>
          {helperText}
        </HelperText>
      )}
    </InputWrapper>
  );
};

export default Input;
